import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageSquare } from 'lucide-react';
import { SERVICES } from '@/data/site';
import { SERVICE_CHALLENGES } from '@/data/services';
import { getEcosystemItems } from '@/data/ecosystem';
import { useLiveTick } from '@/lib/liveContent';
import { Reveal } from '../Reveal';
import { cn } from '@/lib/utils';

const divisionLabel = (key) =>
  key === 'ecosystem' ? 'Amden Ecosystem' : SERVICES.find((s) => s.id === key)?.name || key;

const divisionColor = (key) =>
  key === 'ecosystem' ? '#0B1729' : SERVICES.find((s) => s.id === key)?.color || '#0B1729';

// Genuine catalogue entries (e.g. Amden Compass) become real links to their
// canonical page; uncatalogued service names stay plain text.
const catalogueHref = (name) =>
  getEcosystemItems().find((it) => it.name === name && it.href)?.href || null;

// Section 3 — interactive decision section. Challenge mappings live in
// data/services.js (central, admin-ready); cards are real buttons (keyboard
// + touch), never hover-only.
export const ChallengeFinder = () => {
  useLiveTick();
  const [selectedId, setSelectedId] = useState(null);
  const selected = SERVICE_CHALLENGES.find((c) => c.id === selectedId) || null;

  const discussHref = selected
    ? `/?${
        selected.divisions[0] !== 'ecosystem' ? `service=${selected.divisions[0]}&` : ''
      }source=${encodeURIComponent(`Services Overview — ${selected.label}`)}#solution-finder`
    : null;

  return (
    <section id="by-challenge" aria-labelledby="challenge-heading" data-testid="services-challenges" className="scroll-mt-28 bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="flex items-center gap-3">
            <span className="font-mono text-xs font-medium tracking-widest text-gold-700">03</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-700">
              Find the Right Support
            </span>
          </p>
          <h2 id="challenge-heading" data-testid="challenge-heading" className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
            What would you like to improve?
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
            Choose the challenge that most closely reflects your current priority.
          </p>
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_400px]">
          <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Business challenges">
            {SERVICE_CHALLENGES.map((c, i) => {
              const active = selectedId === c.id;
              return (
                <Reveal key={c.id} delay={(i % 2) * 0.04}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setSelectedId(active ? null : c.id)}
                    data-testid={`challenge-${c.id}`}
                    className={cn(
                      'h-full w-full rounded-lg border-2 px-5 py-4 text-left text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-2',
                      active
                        ? 'border-navy-900 bg-navy-900 text-white'
                        : 'border-slate-200 bg-white text-navy-900 hover:border-navy-300 hover:bg-navy-50'
                    )}
                  >
                    {c.label}
                  </button>
                </Reveal>
              );
            })}
          </div>

          <div aria-live="polite" className="lg:sticky lg:top-28 lg:self-start">
            {selected ? (
              <div className="rounded-2xl border border-slate-200 bg-mist p-7" data-testid="challenge-result">
                <div className="flex flex-wrap gap-2">
                  {selected.divisions.map((d) => (
                    <span
                      key={d}
                      className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white"
                      style={{ backgroundColor: divisionColor(d) }}
                    >
                      {divisionLabel(d)}
                    </span>
                  ))}
                </div>
                <h3 className="mt-4 text-lg font-extrabold tracking-tight text-navy-900">
                  Recommended route
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{selected.explanation}</p>
                <p className="mt-5 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                  Relevant services
                </p>
                <ul className="mt-2 space-y-1.5">
                  {selected.services.map((s) => {
                    const href = catalogueHref(s);
                    return (
                      <li key={s} className="flex items-center gap-2 text-sm font-semibold text-navy-800">
                        <ArrowRight className="h-3.5 w-3.5 text-gold-700" aria-hidden="true" />
                        {href ? (
                          <Link
                            to={href}
                            data-testid={`challenge-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            className="rounded-sm underline-offset-4 transition-colors hover:text-gold-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900"
                          >
                            {s}
                          </Link>
                        ) : (
                          s
                        )}
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-7 flex flex-col gap-2.5">
                  <Link
                    to={selected.exploreHref}
                    data-testid="challenge-explore-link"
                    className="group inline-flex items-center justify-center gap-2 rounded-md bg-navy-900 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-700"
                  >
                    Explore This Service
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
                  </Link>
                  <Link
                    to={discussHref}
                    data-testid="challenge-discuss-link"
                    className="inline-flex items-center justify-center gap-2 rounded-md border-2 border-navy-900 px-5 py-3 text-sm font-bold text-navy-900 transition-colors hover:bg-navy-900 hover:text-white"
                  >
                    <MessageSquare className="h-4 w-4" aria-hidden="true" />
                    Discuss This Challenge
                  </Link>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-mist p-7 text-center" data-testid="challenge-placeholder">
                <p className="text-sm font-semibold text-slate-500">
                  Select a challenge to see the recommended Amden service area and the most relevant
                  services.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
